// visual_bridge — ce que l'aperçu et la console se disent, et ce qu'ils refusent.
//
// ── POURQUOI UN PONT ────────────────────────────────────────────────────────
//
// L'aperçu est une iframe d'une AUTRE origine : la console ne peut ni lire son DOM ni dessiner
// dedans. C'est donc l'app qui dessine ses poignées — même repère que l'image, aucune dérive au
// défilement — et elle doit DIRE à la console ce que l'opérateur vient de faire. Un
// `postMessage`, dans les deux sens.
//
// ── CE FICHIER EST UNE FRONTIÈRE DE SÛRETÉ ──────────────────────────────────
//
// Un écouteur `postMessage` non validé, livré dans CHAQUE app, c'est une surface d'attaque dans
// chaque app. Le dépôt a déjà résolu ce problème pour OAuth (oauth_popup.readAuthMessage) et on
// suit sa forme :
//
//   • une ÉTIQUETTE, pour qu'un message de passage ne soit jamais pris pour autre chose ;
//   • l'ORIGINE comparée à une liste PERMISE, jamais devinée ;
//   • la FENÊTRE d'envoi comparée à celle qu'on attend — deux conditions indépendantes, parce
//     qu'une autre iframe de la même origine n'a aucune raison de parler à notre place ;
//   • tout le reste rend `null`, donc est ignoré, sans exception ;
//   • PUR : la fenêtre et l'événement sont injectés, donc le contrôle d'origine s'éprouve sans
//     navigateur — c'est la seule façon de le tester, et c'est la partie qui compte.
//
// ── ET POURQUOI LES BORNES NE SONT PAS ICI ──────────────────────────────────
//
// Les valeurs permises (0–100 %, le plancher de largeur, les bornes de ratio) sont tenues par
// visual_doc, du côté qui ÉCRIT. Ce module ne garde que la FORME : une sorte connue, un nom qui
// est une chaîne, une retouche qui est un objet, et aucun champ qu'on ne connaît pas. Deux
// juges pour les mêmes bornes finiraient par divergence.

import { type OverridePatch, type PictureOverride, type VisualDoc, cleanPatch, parseVisual } from "./visual_doc";

/** L'étiquette. Elle nomme la maison ET la fonction : une étiquette générique serait
 *  exactement ce qu'un autre outil de la page emploierait. */
export const VISUAL_BRIDGE_TAG = "perimetr:visual-edit";

/** Une boîte mesurée dans la page, telle qu'elle passe sur le fil.
 *
 *  LE JUGEMENT N'EST PAS ICI : l'app rapporte des nombres, et la console décide avec les règles
 *  éprouvées de viewport_audit. Deux juges pour la même mesure finiraient par divergence. */
export type MeasuredBox = {
  readonly label: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
};

/** Une page mesurée à UNE largeur. Même forme que le `WidthMeasure` de la console, qui la juge —
 *  l'accord est tenu par un test de parité, les deux paquets ne s'important pas. */
export type MeasuredPage = {
  readonly width: number;
  readonly scrollWidth: number;
  readonly clientWidth: number;
  readonly boxes: readonly MeasuredBox[];
};

/** Combien de boîtes on accepte d'une autre fenêtre.
 *
 *  Ce ne sont pas des bornes de VALEUR — celles-là sont à visual_doc, du côté qui écrit. C'est
 *  la TAILLE du message, donc sa forme, donc ici : une autre origine ne nous tend pas un tableau
 *  sans fin, et une étiquette qui finit affichée dans la console ne fait pas un mégaoctet.
 *
 *  Large exprès : `body *` sur une vraie page en donne des centaines, et une borne trop basse
 *  tronquerait silencieusement la mesure — donc éteindrait la garde sans rien dire. */
export const MEASURE_MAX_BOXES = 1_500;
export const MEASURE_MAX_LABEL = 160;

/** Ce que l'aperçu dit à la console. */
export type ToConsole =
  | { readonly kind: "ready" }
  | { readonly kind: "picked"; readonly name: string; readonly override: PictureOverride }
  | {
      readonly kind: "commit";
      readonly name: string;
      readonly patch: OverridePatch;
      /** LES DEUX ÉTATS, pour que la console puisse VÉRIFIER avant d'enregistrer : l'état
       *  enregistré, et celui que la retouche produirait.
       *
       *  Pourquoi l'app les envoie plutôt que la console les tienne : l'app les a déjà, elle
       *  rend à partir d'eux, et elle est seule à savoir ce que `withOverride` a borné. La
       *  console devrait sinon refaire cette fusion — une seconde version des mêmes bornes —
       *  ou le serveur devrait offrir une lecture qu'il n'a pas.
       *
       *  OPTIONNEL, et c'est la compatibilité : une app déployée avant cette version envoie un
       *  `commit` sans eux. Son absence doit se DIRE, et non ressembler à deux documents vides —
       *  deux documents vides se comparent sans différence, donc passeraient pour un examen
       *  réussi alors qu'aucun examen n'a eu lieu. */
      readonly docs?: { readonly before: VisualDoc; readonly after: VisualDoc };
    }
  | { readonly kind: "measured"; readonly id: number; readonly page: MeasuredPage };

/** Ce que la console dit à l'aperçu. Sortes DISJOINTES de celles du dessus : un message
 *  renvoyé en écho ne doit pas pouvoir traverser dans le mauvais sens. */
export type ToApp =
  | { readonly kind: "edit"; readonly on: boolean }
  | { readonly kind: "doc"; readonly doc: VisualDoc }
  // RECHARGER LA PAGE, et c'est l'APP qui le fait.
  //
  // Le gabarit coupe le rechargement à chaud (`hmr: false`, platform/vite.config.ts) parce que
  // son WebSocket ne traverse pas notre proxy, et son commentaire dit « the operator's preview is
  // re-deployed, not hot-patched ». Mais rien ne rechargeait le cadre : Workspace.tsx le garde
  // monté exprès, pour ne pas perdre la page où l'opérateur se trouve. Sa plainte du 2026-10-06 :
  // « whenever i do a modification it does not appear … until i hard refresh ».
  //
  // La console ne PEUT pas recharger une iframe d'une autre origine, et réécrire son `src` la
  // ramènerait à l'accueil. L'app, elle, recharge SA page — donc elle garde le chemin. D'où un
  // message, et pas un geste de la console.
  //
  // AUCUN CHAMP : pas de chemin, pas d'horodatage. L'app recharge là où elle est ; un chemin venu
  // de la console serait justement ce qu'on refuse, puisqu'elle ne sait pas où l'opérateur en est.
  | { readonly kind: "reload" }
  | {
      readonly kind: "measure";
      readonly id: number;
      /** LE DOCUMENT VOYAGE AVEC LA DEMANDE, et c'est ce qui rend la mesure fiable.
       *
       *  La version d'avant envoyait `doc` puis `measure`, et l'app attendait deux trames
       *  d'animation pour laisser la disposition se poser. MESURÉ le 2026-10-06
       *  (platform-ui/scripts/verify_offscreen_frame.ts) : dans une iframe hors de l'écran et
       *  d'une autre origine — exactement notre cas — la PREMIÈRE trame arrive, la seconde
       *  JAMAIS. Toute la garde se taisait donc en silence : chaque mesure expirait, l'absence
       *  de preuve valait accord, et « on refuse ce qui casse » devenait « on accepte tout ».
       *
       *  Les deux en un seul message, l'app pose l'état et mesure dans le même rendu — React
       *  dit lui-même quand le DOM est à jour (useLayoutEffect), et plus rien ne dépend d'une
       *  trame que le navigateur a le droit de ne pas produire. */
      readonly doc: VisualDoc;
    };

export const readyMessage = () => ({ tag: VISUAL_BRIDGE_TAG, kind: "ready" as const });

export const pickedMessage = (name: string, override: PictureOverride) =>
  ({ tag: VISUAL_BRIDGE_TAG, kind: "picked" as const, name, override });

export const commitMessage = (
  name: string,
  patch: OverridePatch,
  docs?: { before: VisualDoc; after: VisualDoc },
) => ({ tag: VISUAL_BRIDGE_TAG, kind: "commit" as const, name, patch, ...(docs ? { docs } : {}) });

export const editModeMessage = (on: boolean) => ({ tag: VISUAL_BRIDGE_TAG, kind: "edit" as const, on });

export const docMessage = (doc: VisualDoc) => ({ tag: VISUAL_BRIDGE_TAG, kind: "doc" as const, doc });

export const reloadMessage = () => ({ tag: VISUAL_BRIDGE_TAG, kind: "reload" as const });

export const measureMessage = (id: number, doc: VisualDoc) =>
  ({ tag: VISUAL_BRIDGE_TAG, kind: "measure" as const, id, doc });

export const measuredMessage = (id: number, page: MeasuredPage) =>
  ({ tag: VISUAL_BRIDGE_TAG, kind: "measured" as const, id, page });

/** Un événement de message, réduit à ce dont la décision dépend. */
export type BridgeEvent = { readonly origin: string; readonly data: unknown; readonly source: unknown };

/** À qui on accepte de parler. `source` absent ⇒ on refuse tout : une iframe pas encore montée
 *  ne donne personne à qui faire confiance, donc personne. */
export type BridgeTrust = { readonly origins: readonly string[]; readonly source: unknown };

/** Les DEUX conditions, plus l'étiquette. Rend la charge, ou `null`. */
function enveloppe(event: BridgeEvent, trust: BridgeTrust): Record<string, unknown> | null {
  if (trust.source === null || trust.source === undefined) return null;
  if (event.source !== trust.source) return null;
  if (!trust.origins.includes(event.origin)) return null;
  const d = event.data;
  if (d === null || typeof d !== "object" || Array.isArray(d)) return null;
  const o = d as Record<string, unknown>;
  if (o.tag !== VISUAL_BRIDGE_TAG) return null;
  return o;
}

/** Le nettoyage d'une retouche vient de visual_doc, là où les champs sont définis : le pont et
 *  l'endpoint HTTP le partagent, donc un champ de plus est connu des deux à la fois. */
const patchPropre = cleanPatch;

const nomPropre = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);

/** Un objet simple, ou `null`. Un tableau n'en est pas un : il porte des champs par index, et
 *  l'accepter laisserait passer une forme qui n'est pas celle qu'on attend. */
const objet = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

/** Un vrai nombre. `Number.isFinite` et pas `typeof` : `NaN` et `Infinity` sont des `number` qui
 *  traversent tous les calculs sans rien casser et sans rien mesurer. */
const chiffre = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** L'IDENTIFIANT D'UNE MESURE, et il porte la correction d'un vrai défaut : on mesure à trois
 *  largeurs l'une après l'autre, et sans lui la réponse tardive de la première serait lue comme
 *  la mesure de la deuxième. Le verdict porterait alors sur une page qui n'a jamais existé. */
const identifiant = (v: unknown): number | null => chiffre(v);

/** Une boîte, ou `null`. Une boîte abîmée est JETÉE par l'appelant, et le message survit : la
 *  perdre coûte la vérification d'un élément, perdre le message coûte celle de toute la page. */
function boitePropre(v: unknown): MeasuredBox | null {
  if (v === null || typeof v !== "object" || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  // Une étiquette VIDE reste valable : `body > div` sans classe ni texte en donne une, et c'est
  // souvent l'élément qui porte la mise en page.
  if (typeof o.label !== "string") return null;
  const x = chiffre(o.x), y = chiffre(o.y), width = chiffre(o.width), height = chiffre(o.height);
  if (x === null || y === null || width === null || height === null) return null;
  // Recopié champ par champ, jamais étalé : un `onclick` ou un `__proto__` venu d'une autre
  // fenêtre n'a aucune raison de traverser.
  return { label: o.label.slice(0, MEASURE_MAX_LABEL), x, y, width, height };
}

/** Une page mesurée, ou `null`.
 *
 *  UN CHIFFRE MANQUANT REFUSE LE MESSAGE ENTIER, et c'est voulu : sans `scrollWidth` il n'y a
 *  pas de mesure du tout, et la deviner inventerait l'état de la page. La console n'aura alors
 *  rien à comparer, donc elle acceptera la retouche — c'est le repli assumé du dispositif, écrit
 *  dans visual_verdict : on ne refuse jamais le geste de l'opérateur sur une absence de preuve. */
function pagePropre(v: unknown): MeasuredPage | null {
  if (v === null || typeof v !== "object" || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const width = chiffre(o.width), scrollWidth = chiffre(o.scrollWidth), clientWidth = chiffre(o.clientWidth);
  if (width === null || scrollWidth === null || clientWidth === null) return null;
  const brutes = Array.isArray(o.boxes) ? o.boxes : [];
  const boxes: MeasuredBox[] = [];
  // On borne AVANT de nettoyer : nettoyer un million de boîtes pour en jeter la plupart serait
  // déjà le travail qu'on refuse de faire.
  for (const b of brutes.slice(0, MEASURE_MAX_BOXES)) {
    const propre = boitePropre(b);
    if (propre !== null) boxes.push(propre);
  }
  return { width, scrollWidth, clientWidth, boxes };
}

/** Un message de l'APERÇU vers la console, validé, ou `null`. */
export function readToConsole(event: BridgeEvent, trust: BridgeTrust): ToConsole | null {
  const o = enveloppe(event, trust);
  if (o === null) return null;
  if (o.kind === "ready") return { kind: "ready" };
  if (o.kind === "picked") {
    const name = nomPropre(o.name);
    const override = patchPropre(o.override);
    if (name === null || override === null) return null;
    return { kind: "picked", name, override: override as PictureOverride };
  }
  if (o.kind === "commit") {
    const name = nomPropre(o.name);
    const patch = patchPropre(o.patch);
    if (name === null || patch === null) return null;
    // LES DEUX DOCUMENTS OU AUCUN. Un seul ne sert à rien — il n'y a pas de comparaison à
    // faire — et le faire passer pour un examen serait pire que de l'annoncer absent.
    // `parseVisual` ne lève jamais et borne tout, comme pour la sorte `doc`.
    const docs = objet(o.docs);
    if (docs === null) return { kind: "commit", name, patch };
    if (objet(docs.before) === null || objet(docs.after) === null) return { kind: "commit", name, patch };
    return {
      kind: "commit", name, patch,
      docs: { before: parseVisual(docs.before), after: parseVisual(docs.after) },
    };
  }
  if (o.kind === "measured") {
    const id = identifiant(o.id);
    const page = pagePropre(o.page);
    if (id === null || page === null) return null;
    return { kind: "measured", id, page };
  }
  // Une sorte inconnue : on n'invente aucun comportement.
  return null;
}

/** Un message de la CONSOLE vers l'aperçu, validé, ou `null`. */
export function readToApp(event: BridgeEvent, trust: BridgeTrust): ToApp | null {
  const o = enveloppe(event, trust);
  if (o === null) return null;
  if (o.kind === "edit") {
    // Un BOOLÉEN, pas une valeur « vraie » : « oui » ou `1` viennent d'un appelant qui n'a pas
    // le même contrat que nous, et le deviner masquerait le désaccord.
    if (typeof o.on !== "boolean") return null;
    return { kind: "edit", on: o.on };
  }
  if (o.kind === "doc") {
    // LE MÊME GARDE QUE LE FICHIER : le document arrive d'une autre fenêtre, donc il passe par
    // `parseVisual` — borné, sans clé étrange, et jamais une erreur. Un document illisible ne
    // doit pas figer l'aperçu : il ne retouche simplement rien.
    return { kind: "doc", doc: parseVisual(o.doc) };
  }
  // Aucun champ à valider : la sorte EST le message. On rend une forme neuve plutôt que `o`,
  // pour qu'un champ de passage ne traverse pas avec elle.
  if (o.kind === "reload") return { kind: "reload" };
  if (o.kind === "measure") {
    const id = identifiant(o.id);
    if (id === null) return null;
    // LE MÊME GARDE QUE LA SORTE `doc` : le document arrive d'une autre fenêtre, donc il passe
    // par `parseVisual` — borné, sans clé étrange, et jamais une erreur.
    return { kind: "measure", id, doc: parseVisual(o.doc) };
  }
  return null;
}
